import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';

import {
  deleteFavoriteBreadcrumbs,
  getFavoriteBreadcrumbs,
  getHoveredUser,
  getUser,
  postFavoriteBreadcrumbs,
} from '../../api/users';

import {
  initialHoveredUser,
  initialUserState,
} from '../../components/Users/constants/initialStates';
import { REQUEST } from '../../constants/request';

import { pathForTag } from '../../utils/pathForTag';
import { INITIAL_LOADING_STATE } from '../../utils/asyncThunkRequest';

export const fetchUser = createAsyncThunk(
  `users/fetchUser`,
  async (currentUser, { signal, rejectWithValue }) => {
    const source = axios.CancelToken.source();
    signal.addEventListener('abort', () => {
      source.cancel();
    });
    try {
      const response = await getUser(currentUser, {
        cancelToken: source.token,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.status);
    }
  },
  {
    condition: ({ userId }, { getState }) => {
      const { users, requestIdLoadingStatus } = getState().users;
      const userRequestStatus = requestIdLoadingStatus[userId];

      return !(users.byId[userId] || userRequestStatus === 'pending');
    },
  },
);

export const fetchHoveredUser = createAsyncThunk(
  `users/fetchHoveredUser`,
  async (hoveredUserData, { signal, rejectWithValue }) => {
    const source = axios.CancelToken.source();
    signal.addEventListener('abort', () => {
      source.cancel();
    });
    try {
      const response = await getHoveredUser(hoveredUserData, {
        cancelToken: source.token,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.status);
    }
  },
);

export const fetchFavoriteBreadcrumbs = createAsyncThunk(
  'breadcrumbs/fetchFavoriteBreadcrumbs',
  async (_, { signal, rejectWithValue }) => {
    const source = axios.CancelToken.source();
    signal.addEventListener('abort', () => {
      source.cancel();
    });
    try {
      const response = await getFavoriteBreadcrumbs({
        cancelToken: source.token,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.status);
    }
  },
  {
    condition: (_, { getState }) => {
      const { currentLoadings } = getState().users.meBreadcrumbs;

      return !(currentLoadings?.channels === REQUEST.pending);
    },
  },
);

export const addFavoriteBreadcrumb = createAsyncThunk(
  'breadcrumbs/addFavoriteBreadcrumb',
  async (data, { signal, rejectWithValue }) => {
    const source = axios.CancelToken.source();
    signal.addEventListener('abort', () => {
      source.cancel();
    });
    try {
      const response = await postFavoriteBreadcrumbs(data, {
        cancelToken: source.token,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.status);
    }
  },
  {
    condition: (data, { getState }) => {
      const { currentLoadings } = getState().users.meBreadcrumbs;

      return !(currentLoadings?.[pathForTag(data)] === REQUEST.pending);
    },
  },
);

export const removeFavoriteBreadcrumb = createAsyncThunk(
  'breadcrumbs/removeFavoriteBreadcrumb',
  async (data, { signal, rejectWithValue }) => {
    const source = axios.CancelToken.source();
    signal.addEventListener('abort', () => {
      source.cancel();
    });
    try {
      const response = await deleteFavoriteBreadcrumbs(data, {
        cancelToken: source.token,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.status);
    }
  },
  {
    condition: (data, { getState }) => {
      const { currentLoadings } = getState().users.meBreadcrumbs;

      return !(currentLoadings?.[pathForTag(data)] === REQUEST.pending);
    },
  },
);

const INITIAL_STATE = {
  users: {
    byId: {},
    allIds: [],
  },
  meBreadcrumbs: {
    channels: [],
    currentRequestIds: {},
    currentLoadings: {},
    currentErrors: {},
  },
  hoveredUsers: {
    byId: {},
    request: {},
  },
  currentRequestId: {},
  requestIdLoadingStatus: {},
  error: null,
};

export const UsersSlice = createSlice({
  name: 'users',
  initialState: INITIAL_STATE,
  reducers: {
    addRoles: (state, { payload }) => {
      const { userId, allRoles } = payload;
      const arr = [];
      allRoles.forEach(role =>
        arr.push({
          id: role,
          value: role,
        }),
      );
      state.users.byId[userId].baseTags.roles = { children: arr };
    },
    resetUsersSlice: () => INITIAL_STATE,
  },
  extraReducers: builder => {
    builder.addCase(fetchHoveredUser.pending, (state, { meta }) => {
      const { userId, orgId, streamId, channelId } = meta.arg;
      const key = `${orgId}/${streamId}/${channelId}/${userId}`;
      state.hoveredUsers.byId[key] = {
        users: [],
        channelUserdata: {
          userSubscriptions: [],
          hasFollowed: false,
          hasBookmarked: false,
          channelCID: {
            orgId,
            streamId,
            channelId,
          },
          userRoleMapping: {
            userId,
            roles: [],
            mapping: [],
          },
        },
        subscriptions: [],
        streamUserData: {
          streamCID: {
            orgId,
            streamId,
          },
          userChannels: [],
          userRoleMapping: {
            userId,
            roles: [],
            mapping: [],
          },
          hasBookmarked: false,
        },
        channel: {
          locations: [],
          topics: [],
          name: channelId,
          orgId,
          streamId,
          id: channelId,
          visible: true,
          description: '',
          boosts: {},
          restricted: false,
          metadata: {},
        },
      };
      state.hoveredUsers.request[key].loading = 'pending';
      state.hoveredUsers.request[key].error = undefined;
      state.hoveredUsers.request[key].currentRequestId = meta.requestId;
    });
    builder.addCase(fetchHoveredUser.fulfilled, (state, { payload, meta }) => {
      const { requestId } = meta;
      const { userId, orgId, streamId, channelId } = meta.arg;
      const key = `${orgId}/${streamId}/${channelId}/${userId}`;
      if (
        state.hoveredUsers.request[key].loading === 'pending' &&
        state.hoveredUsers.request[key].currentRequestId === requestId
      ) {
        state.hoveredUsers.byId[key] = { ...payload };
        state.hoveredUsers.request[key].loading = 'idle';
        state.hoveredUsers.request[key].currentRequestId = undefined;
      }
    });
    builder.addCase(fetchHoveredUser.rejected, (state, { meta, payload }) => {
      const { userId, orgId, streamId, channelId } = meta.arg;
      const key = `${orgId}/${streamId}/${channelId}/${userId}`;
      state.hoveredUsers.request[key].loading = 'idle';
      state.hoveredUsers.request[key].error = payload;
      state.hoveredUsers.request[key].currentRequestId = undefined;
    });
    builder.addCase(fetchUser.pending, (state, action) => {
      state.currentRequestId[action.meta.arg.userId] = action.meta.requestId;
      state.requestIdLoadingStatus[action.meta.arg.userId] = 'pending';
    });
    builder.addCase(fetchUser.fulfilled, (state, action) => {
      const { meta, payload } = action;
      const { requestId } = meta;
      if (
        state.requestIdLoadingStatus[action.meta.arg.userId] === 'pending' &&
        state.currentRequestId[action.meta.arg.userId] === requestId
      ) {
        state.users.byId[payload.id] = { ...initialUserState, ...payload };
        state.users.allIds.push(payload.id);
        delete state.currentRequestId[action.meta.arg.userId];
        delete state.requestIdLoadingStatus[action.meta.arg.userId];
      }
    });
    builder.addCase(fetchUser.rejected, (state, action) => {
      const { requestId } = action.meta;
      if (
        state.requestIdLoadingStatus[action.meta.arg.userId] === 'pending' &&
        state.currentRequestId[action.meta.arg.userId] === requestId
      ) {
        state.error = action.payload;
        delete state.currentRequestId[action.meta.arg.userId];
        delete state.requestIdLoadingStatus[action.meta.arg.userId];
      }
    });
    builder.addCase(fetchFavoriteBreadcrumbs.pending, (state, { meta }) => {
      state.meBreadcrumbs.currentRequestIds.channels = meta.requestId;
      state.meBreadcrumbs.currentLoadings.channels = REQUEST.pending;
      delete state.meBreadcrumbs.currentErrors.channels;
    });
    builder.addCase(
      fetchFavoriteBreadcrumbs.fulfilled,
      (state, { meta, payload }) => {
        if (
          state.meBreadcrumbs.currentLoadings.channels === REQUEST.pending &&
          state.meBreadcrumbs.currentRequestIds.channels === meta.requestId
        ) {
          state.meBreadcrumbs.channels = payload.channels?.map(channel =>
            pathForTag(channel),
          );
          state.meBreadcrumbs.currentLoadings.channels = REQUEST.idle;
          delete state.meBreadcrumbs.currentRequestIds.channels;
        }
      },
    );
    builder.addCase(
      fetchFavoriteBreadcrumbs.rejected,
      (state, { meta, payload }) => {
        if (
          state.meBreadcrumbs.currentLoadings.channels === REQUEST.pending &&
          state.meBreadcrumbs.currentRequestIds.channels === meta.requestId
        ) {
          state.meBreadcrumbs.currentErrors.channels = payload;
          delete state.meBreadcrumbs.currentLoadings.channels;
          delete state.meBreadcrumbs.currentRequestIds.channels;
        }
      },
    );
    builder.addCase(addFavoriteBreadcrumb.pending, (state, { meta }) => {
      const UID = pathForTag(meta.arg);
      state.meBreadcrumbs.currentRequestIds[UID] = meta.requestId;
      state.meBreadcrumbs.currentLoadings[UID] = REQUEST.pending;
      delete state.meBreadcrumbs.currentErrors.channels;
    });
    builder.addCase(addFavoriteBreadcrumb.fulfilled, (state, { meta }) => {
      const UID = pathForTag(meta.arg);
      if (
        state.meBreadcrumbs.currentLoadings[UID] === REQUEST.pending &&
        state.meBreadcrumbs.currentRequestIds[UID] === meta.requestId
      ) {
        state.meBreadcrumbs.channels.push(UID);
        state.meBreadcrumbs.currentLoadings[UID] = REQUEST.idle;
        delete state.meBreadcrumbs.currentRequestIds[UID];
      }
    });
    builder.addCase(
      addFavoriteBreadcrumb.rejected,
      (state, { meta, payload }) => {
        const UID = pathForTag(meta.arg);
        if (
          state.meBreadcrumbs.currentLoadings[UID] === REQUEST.pending &&
          state.meBreadcrumbs.currentRequestIds[UID] === meta.requestId
        ) {
          state.meBreadcrumbs.currentErrors[UID] = payload;
          delete state.meBreadcrumbs.currentLoadings[UID];
          delete state.meBreadcrumbs.currentRequestIds[UID];
        }
      },
    );
    builder.addCase(removeFavoriteBreadcrumb.pending, (state, { meta }) => {
      const UID = pathForTag(meta.arg);
      state.meBreadcrumbs.currentRequestIds[UID] = meta.requestId;
      state.meBreadcrumbs.currentLoadings[UID] = REQUEST.pending;
      delete state.meBreadcrumbs.currentErrors.channels;
    });
    builder.addCase(removeFavoriteBreadcrumb.fulfilled, (state, { meta }) => {
      const UID = pathForTag(meta.arg);
      if (
        state.meBreadcrumbs.currentLoadings[UID] === REQUEST.pending &&
        state.meBreadcrumbs.currentRequestIds[UID] === meta.requestId
      ) {
        state.meBreadcrumbs.channels = state.meBreadcrumbs.channels.filter(
          channel => channel !== UID,
        );
        state.meBreadcrumbs.currentLoadings[UID] = REQUEST.idle;
        delete state.meBreadcrumbs.currentRequestIds[UID];
      }
    });
    builder.addCase(
      removeFavoriteBreadcrumb.rejected,
      (state, { meta, payload }) => {
        const UID = pathForTag(meta.arg);
        if (
          state.meBreadcrumbs.currentLoadings[UID] === REQUEST.pending &&
          state.meBreadcrumbs.currentRequestIds[UID] === meta.requestId
        ) {
          state.meBreadcrumbs.currentErrors[UID] = payload;
          delete state.meBreadcrumbs.currentLoadings[UID];
          delete state.meBreadcrumbs.currentRequestIds[UID];
        }
      },
    );
  },
});

export const selectorGetUser = userId => state =>
  state.users.users.byId[userId] || initialUserState;
export const selectorByIdUsers = state => state.users.users.byId || {};
export const selectorGetHoveredUser = (state, userKey) =>
  state.users.hoveredUsers.byId[userKey] || initialHoveredUser;
export const selectorGetHoveredUserRequest = (state, userKey) =>
  state.users.hoveredUsers.request[userKey] || {
    ...INITIAL_LOADING_STATE,
  };
export const selectorGetFavoriteBreadcrumbChannels = state =>
  state.users.meBreadcrumbs.channels || [];
export const selectorFavoriteBreadcrumbLoadingState = (state, breadcrumb) =>
  state.users.meBreadcrumbs.currentLoadings[breadcrumb];

export const { addRoles, resetUsersSlice } = UsersSlice.actions;

export default UsersSlice.reducer;
